    import React, { useState, useRef } from 'react';
    import usePDA1080_R01Store from "./usePDA1080_R01Store";
    import { usePDA1080_R01Content } from "./PDA1080_R01Content";

    /**
     * PDA1080_R01 자재반입 입력 폼
     * PDA1040_R01 디자인 스타일 적용
     */
    const PDA1080_R01Form = () => {
    const { 
        lotNo, 
        setLotNo, 
        scannedLot,
        returnQty,
        setReturnQty,
        loading,
    } = usePDA1080_R01Store();

    // lotInputRef를 Content Hook에 전달
    const lotInputRef = useRef<HTMLInputElement>(null);
    const { handleScanLot, handleAddToGrid } = usePDA1080_R01Content(lotInputRef);
    
    // 키보드 제어
    const [keyboardEnabled, setKeyboardEnabled] = useState(false);
    const [lastTapTime, setLastTapTime] = useState(0);

    // 더블탭으로 키보드 활성화
    const handleDoubleTap = () => {
        const now = Date.now();
        const timeDiff = now - lastTapTime;
        
        if (timeDiff < 300) {
        setKeyboardEnabled(true);
        lotInputRef.current?.focus();
        }
        
        setLastTapTime(now);
    };

    // 토글 버튼으로 키보드 제어
    const toggleKeyboard = () => {
        setKeyboardEnabled(!keyboardEnabled);
        if (!keyboardEnabled) {
        setTimeout(() => lotInputRef.current?.focus(), 100);
        }
    };

    // Enter 키 처리
    const handleLotNoKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
        e.preventDefault();
        handleScanLot();
        }
    };

    const handleQtyKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
        e.preventDefault();
        handleAddToGrid();
        }
    };

    return (
        <div className="w-full bg-white p-4   border-b border-gray-200">
        <div className="space-y-1">
            {/* LOT No */}
            <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">LOT No</label>
            <div className="flex-1 flex gap-1">
                <input
                ref={lotInputRef}
                type="text"
                value={lotNo}
                onChange={(e) => setLotNo(e.target.value)}
                onKeyDown={handleLotNoKeyDown}
                onClick={handleDoubleTap}
                inputMode={keyboardEnabled ? 'text' : 'none'}
                disabled={loading}
                placeholder="LOT 번호 스캔"
                autoComplete="off"
                className="flex-1 max-w-[173px] px-2  text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                type="button"
                onClick={toggleKeyboard}
                className={`w-10 flex-shrink-0 px-2  border border-gray-300 rounded transition-colors ${
                    keyboardEnabled
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title={keyboardEnabled ? '키보드 숨기기' : '키보드 표시'}
                >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                </button>
            </div>
            </div>

            {/* 품목 */}
            <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">품목</label>
            <div className="flex-1 flex gap-1">
                <input
                type="text"
                value={scannedLot?.itemNm || ''}
                readOnly
                className="flex-1 max-w-[173px] px-2  text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
                />
                <div className="w-10 flex-shrink-0"></div>
            </div>
            </div>

            {/* 규격 */}
            <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">규격</label>
            <div className="flex-1 flex gap-1">
                <input
                type="text"
                value={scannedLot?.itemSpec || ''}
                readOnly
                className="flex-1 max-w-[173px] px-2  text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
                />
                <div className="w-10 flex-shrink-0"></div>
            </div>
            </div>

            {/* LOT재고 */}
            <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">LOT재고</label>
            <div className="flex-1 flex gap-1">
                <input
                type="text"
                value={scannedLot ? `${scannedLot.lotQty.toLocaleString()}` : ''}
                readOnly
                placeholder="ea"
                className="flex-1 max-w-[173px] px-2  text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
                />
                <span className="px-1  text-lg text-black flex items-center"></span>
                <div className="w-10 flex-shrink-0"></div>
            </div>
            </div>

            {/* 보관위치 */}
            <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">보관위치</label>
            <div className="flex-1 flex gap-1">
                <input
                type="text"
                value={scannedLot?.warehouseNm || ''}
                readOnly
                className="flex-1 max-w-[173px] px-2  text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
                />
                <div className="w-10 flex-shrink-0"></div>
            </div>
            </div>

            {/* 반입 수량 및 저장 버튼 */}
            <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">반입량</label>
            <div className="flex-1 flex gap-1">
                <input
                type="number"
                value={returnQty || ''}
                onChange={(e) => setReturnQty(Number(e.target.value))}
                onKeyDown={handleQtyKeyDown}
                disabled={loading || !scannedLot}
                placeholder="수량"
                className="w-20 px-2  text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                min="0"
                step="1"
                />
                <button
                onClick={handleAddToGrid}
                disabled={loading || !scannedLot || !returnQty || returnQty <= 0}
                className="flex-1 bg-blue-500 text-white px-2  rounded text-lg font-medium hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                검수
                </button>
            </div>
            </div>

        </div>
        </div>
    );
    };

    export default PDA1080_R01Form;
